import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, MapPin, Clock } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { SubpageHeader } from "@/components/SubpageHeader";
import { EASE } from "@/components/Reveal";
import { JOB_BRAND } from "./Careers";

const INITIAL = { name: "", email: "", phone: "", resume_url: "", linkedin_url: "", cover: "" };
const Label = ({ children }) => <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{children}</span>;

const ApplyForm = ({ job }) => {
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, linkedin_url: form.linkedin_url || null };
      await api.post(`/jobs/${job.id}/apply`, payload);
      setDone(true);
      toast.success("Application sent. We'll be in touch.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass rounded-[2rem] p-6 text-white sm:p-8">
      <AnimatePresence mode="wait">
        {done ? (
          <motion.div key="ok" data-testid="apply-success-message" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.6, ease: EASE }} className="flex min-h-[360px] flex-col items-start justify-center">
            <span className="grid h-14 w-14 place-items-center rounded-full bg-brand-cyan text-brand-navy"><Check className="h-6 w-6" /></span>
            <h3 className="mt-8 font-display text-2xl font-bold tracking-tight">Application received.</h3>
            <p className="mt-3 text-sm text-white/60">Thanks for applying for {job.title}. Our team reviews every application and will reply by email within a week.</p>
            <Link to="/careers" data-testid="apply-back-to-careers" className="btn-ghost mt-8"><span>Back to open roles</span></Link>
          </motion.div>
        ) : (
          <motion.form key="form" data-testid="apply-form" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid gap-5">
            <h3 className="font-display text-xl font-semibold">Apply for this role</h3>
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block"><Label>Full name *</Label><input data-testid="apply-name" required minLength={2} value={form.name} onChange={set("name")} className="field" placeholder="Your name" /></label>
              <label className="block"><Label>Email *</Label><input data-testid="apply-email" required type="email" value={form.email} onChange={set("email")} className="field" placeholder="you@email.com" /></label>
              <label className="block"><Label>Phone *</Label><input data-testid="apply-phone" required minLength={6} value={form.phone} onChange={set("phone")} className="field" placeholder="+91 98XXX XXXXX" /></label>
              <label className="block"><Label>LinkedIn (optional)</Label><input data-testid="apply-linkedin" type="url" pattern="https://.*" value={form.linkedin_url} onChange={set("linkedin_url")} className="field" placeholder="https://linkedin.com/in/…" /></label>
            </div>
            <label className="block"><Label>Resume / portfolio link *</Label><input data-testid="apply-resume" required type="url" pattern="https://.*" value={form.resume_url} onChange={set("resume_url")} className="field" placeholder="https://drive.google.com/… (set sharing to “anyone with the link”)" /></label>
            <label className="block"><Label>Why you, for this role? *</Label><textarea data-testid="apply-cover" required minLength={20} rows={5} value={form.cover} onChange={set("cover")} className="field resize-none" placeholder="A few lines on your experience and why this role fits." /></label>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/35">Your details go straight to our hiring team.</p>
              <button data-testid="apply-submit" type="submit" disabled={busy} className="btn-primary disabled:opacity-60"><span>{busy ? "Sending…" : "Submit application"}</span><ArrowRight className="h-4 w-4" /></button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function JobDetail() {
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${jobId}`).then((r) => setJob(r.data)).catch(() => setMissing(true));
  }, [jobId]);

  return (
    <div data-testid="job-detail-page" className="min-h-screen bg-brand-navy text-white">
      <SubpageHeader testId="job-header" back="/careers" backLabel="All open roles" />
      <main className="mx-auto max-w-[1200px] px-6 py-14 lg:py-20">
        {missing && (
          <div data-testid="job-not-found" className="glass rounded-3xl p-10 text-center">
            <h1 className="font-display text-2xl font-bold">This role is no longer open.</h1>
            <Link to="/careers" className="btn-ghost mt-6 inline-flex"><span>See open roles</span></Link>
          </div>
        )}
        {job && (
          <div className="grid gap-12 lg:grid-cols-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: JOB_BRAND[job.brand].color }}><span className="h-2 w-2 rounded-full" style={{ background: JOB_BRAND[job.brand].color }} />{JOB_BRAND[job.brand].label}</div>
              <h1 data-testid="job-title" className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">{job.title}</h1>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/60">
                <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4 text-brand-cyan" />{job.employment_type}</span>
                <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-cyan" />{job.location}</span>
              </div>
              <p className="mt-8 text-base text-white/75 md:text-lg">{job.summary}</p>
              <div className="mt-8 whitespace-pre-wrap text-sm leading-relaxed text-white/65 sm:text-base">{job.description}</div>
              {job.requirements?.length > 0 && (
                <div className="mt-10">
                  <h2 className="font-display text-xl font-semibold">What we're looking for</h2>
                  <ul className="mt-4 space-y-3">
                    {job.requirements.map((r, i) => (
                      <li key={i} className="flex gap-3 text-sm text-white/70 sm:text-base"><span className="mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-brand-cyan/15 text-brand-cyan"><Check className="h-3 w-3" /></span>{r}</li>
                    ))}
                  </ul>
                </div>
              )}
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: EASE }} className="lg:col-span-5">
              <div className="lg:sticky lg:top-8"><ApplyForm job={job} /></div>
            </motion.div>
          </div>
        )}
      </main>
    </div>
  );
}
